import moment from 'moment'
import {FC, MutableRefObject, useCallback, useContext, useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import {PhotoType, PhotoTypes} from 'entities/messages'
import {
  GalleryTypes,
  GetMyMedia,
  openGallery,
  setCurrentImage,
  setGalleryPhotos,
  setGalleryType,
  setMyMediaUp,
  useMyMedia,
  useMyMediaCursor
} from 'entities/profile'
import {AddPhoto, useUser} from 'entities/user'
import {Gallery} from 'features/gallery'
import {MediaUpload} from 'features/mediaUpload'
import {WarningModalContext, WarningModalContextType} from 'features/warningModal/context'
import {GetDirectUploadUrlPhoto, UploadMediaToBucket} from 'shared/api/images'
import {usePrompt, useVirtualScroll} from 'shared/hooks'
import {mediaVariants, skipFalsy} from 'shared/lib'
import {UserStatus} from 'shared/types'
import {Loader} from 'shared/ui/loader'
import styles from './styles.module.scss'

type MyMediaProps = {
  wrapperRef: MutableRefObject<HTMLDivElement | null> | HTMLDivElement | null
}

type LoadingPhotoType = {
  url: string
}

const fileSizeMB = 6

export const MyMedia: FC<MyMediaProps> = ({wrapperRef}) => {
  const dispatch = useDispatch()
  const cursor = useMyMediaCursor()
  const media = useMyMedia()
  const {status} = useUser()
  const {refetch: load, isFetching, isError} = GetMyMedia(dispatch)
  const [loadingPhoto, setLoadingPhoto] = useState<LoadingPhotoType[]>([])
  const {setWarningModal} = useContext(WarningModalContext) as WarningModalContextType

  useVirtualScroll({ref: wrapperRef, isError, isFetching, load, cursor})

  useEffect(() => {
    dispatch(setGalleryPhotos({photos: media, cursor}))
  }, [media, cursor])

  usePrompt('Media is loading. When you exit the chat, the download will be interrupted.', Boolean(loadingPhoto.length))

  const setImages = async (_: any, files: File[]) => {
    if (files.length > 30) return setWarningModal({
      header: 'Too many files',
      text: 'You can upload a maximum of 30 files of any type at a time.'
    })

    if (files.find((file) => file.size > fileSizeMB * 1000 * 1000)) return setWarningModal({
      header: 'File size is too large',
      text: `Maximum file size ${fileSizeMB}mb`
    })

    try {
      const filesLoading: {url: string}[] = files
        .map((file) => ({url: URL.createObjectURL(file)}))
      const filesLoad: string[] = []

      setLoadingPhoto((lastPhoto) => [...filesLoading, ...lastPhoto])

      const {data: directUploadUrls} = await GetDirectUploadUrlPhoto({count: files.length, mediaType: 'public'})

      for (let i = 0; i < files.length; i++) {
        await UploadMediaToBucket(directUploadUrls[i].uploadUrl, files[i])

        filesLoad.unshift(directUploadUrls[i].key)
      }

      const {data: response} = await AddPhoto(filesLoad)
      setLoadingPhoto((lastPhoto) => lastPhoto.filter((photo) => !filesLoading.find((item) => item.url === photo.url)))
      dispatch(setMyMediaUp(response.data))
    } catch (e) {
      console.error(e)
      setWarningModal({header: 'Error', text: 'Error'})
    }
  }

  return (
    <>
      <div className={styles.root}>
        {status === UserStatus.ACTIVE ? <>
          <div className={styles.header}>My photos</div>
          <MediaUpload setImages={setImages} multiple fileSizeMB={fileSizeMB} />
        </> : null}
        {media.length || loadingPhoto.length || isFetching ? (
          <div className={styles.profileMedia}>
            {loadingPhoto.map((photo) => <LoadMedia key={photo.url} url={photo.url} />)}
            {media.map((photo, index) => <Media key={photo.id} index={index} photo={photo} />)}
            {isFetching ? <Loader className={styles.profileMediaLoader} /> : null}
          </div>
        ) : null}
      </div>
      <Gallery load={load} isFetching={isFetching} isError={isError} />
    </>
  )
}

type MediaProps = {
  photo: PhotoType
  index: number
}

const Media: FC<MediaProps> = ({photo, index}) => {
  const dispatch = useDispatch()
  const media = useMyMedia()
  const user = useUser()

  const openGalleryHandler = useCallback((type: PhotoTypes, index: number) => {
    const age = moment().diff(user.profile.dateBirth, 'years')

    dispatch(openGallery())
    dispatch(setGalleryType(GalleryTypes.myProfile))
    dispatch(setCurrentImage({
      index,
      type,
      id: media[index].id,
      src: media[index].image.url,
      label: `${skipFalsy(user.profile.name, String(age)).join(', ')}`
    }))
  }, [media])

  return (
    <div
      className={styles.media}
      onClick={() => openGalleryHandler(photo.type, index)}
    >
      <div
        className={styles.back}
        style={{
          backgroundImage: `url(${photo.image.url + mediaVariants.square256})`
        }}
      />
      <img
        className={styles.content}
        src={photo.image.url + mediaVariants.originalS}
        alt='photo'
      />
    </div>
  )
}

const LoadMedia: FC<{url: string}> = ({url}) => {
  return (
    <div className={styles.media}>
      <div className={styles.back} style={{backgroundImage: `url(${url})`}} />
      <img src={url} alt='laodMedia' className={styles.content} />
      <div className={styles.backLoad} />
      <Loader className={styles.loadMedia} innerColor='white' />
    </div>
  )
}
